import { appInsight, collectProperties } from 'components/appInsight';
import { Layout } from 'components/Layout';
import PageContent from 'components/page-content/sport-info/PageContent';
import { Seo } from 'components/seo';
import { mergeTagsFromSeoData, parseSlugFromParams } from 'components/seo';
import { loadFlat, loadIndex, loadTree, seoItemHierarchy } from 'components/staticMap';
import { AmbienteContext } from 'context/Ambiente';
import { AppFragment } from 'enums/path-fragment';
import { getSportInfoPage, SportInfoPageQuery } from 'lib/datoCms/queries/getSportInfoPage';
import { AllCarrelloErrors, DatoCmsSportManifestazionePage } from 'lib/datoCms/types';
import { cleanConfig } from 'lib/datoCms/utils';
import { ssrLiveNav, useLiveNavSnapshot } from 'lib/ssr/live-nav';
import { GetStaticPathsResult } from 'next';
import { getDatoCMSData } from 'sites/snai/lib/datoCms/queries/getDatoCMSData';
import { SportPageProps } from 'sites/snai/pages/sport';
import { ISR_REVALIDATE } from 'utility/constant';
import { skipStaticPageGeneration } from 'utility/functions';

interface SportInfoPageProps extends Omit<SportPageProps, 'cmsContent'> {
  cmsContent: SportInfoPageQuery;
  staticMenu: seoItemHierarchy;
  allCarrelloErrors: Array<AllCarrelloErrors>;
  manifestazioneDatoCms: DatoCmsSportManifestazionePage;
}

export async function getStaticPaths() {
  const result: GetStaticPathsResult<{ slug: string }> = {
    paths: [],
    fallback: 'blocking',
  };

  if (!skipStaticPageGeneration()) {
    await loadIndex();

    loadFlat(`${AppFragment.Sport}/info`).forEach((x) => {
      const slug = `${x.loc}`.toLowerCase().replace(`/${AppFragment.Sport}/info/`, '');
      result.paths.push({ params: { slug } });
    });
    // result.fallback = true;
    console.log(`## sport.info.getStaticPaths -> ${result.paths.length} items`);
  }

  return result;
}

export const getStaticProps = async (context) => {
  const { locale, params } = context;
  const { slug: key } = params ?? {};

  const slug = parseSlugFromParams(`${AppFragment.Sport}/info/${key}`);

  console.log(`## sport.Info.getStaticProps : ${slug}`);
  try {
    const {
      site,
      header,
      needHelp,
      footer,
      allLabels: labels,
      sidebarInstantGame,
      config,
    } = await getDatoCMSData('common', locale);

    const prLiveNav = ssrLiveNav({ rootSlug: AppFragment.Sport, light: true }, locale);
    const prPageData = getSportInfoPage(slug, locale);

    const [pageData, liveNav] = await Promise.all([prPageData, prLiveNav]);

    const { allMightLikes: mightLikes, tabsWithAccordionModel: seoData, ...pageContent } = pageData ?? {};

    const cmsContent = mergeTagsFromSeoData({
      seoData,
      cmsData: pageContent,
      fieldName: 'sportInfoPage',
      forceUpdate: true,
    });

    await loadIndex();
    return Promise.resolve({
      props: {
        slug,
        site,
        header,
        footer,
        labels,
        config: cleanConfig(config),
        liveNav,
        seoData,
        needHelp,
        cmsContent,
        mightLikes,
        sidebarInstantGame,
        staticMenu: loadTree(`/${AppFragment.Sport}`, 3),
      },
      revalidate: ISR_REVALIDATE * 10,
    });
  } catch (exception) {
    appInsight?.trackException({
      exception,
      id: `/sport/info/${key}/index`,
      properties: collectProperties(true),
    });

    return Promise.resolve({ notFound: true });
  }
};

const SportInfo = ({
  slug: _,
  site,
  header,
  footer,
  liveNav,
  seoData,
  needHelp,
  staticMenu,
  cmsContent,
  mightLikes,
  sidebarInstantGame,
}: SportInfoPageProps) => {
  useLiveNavSnapshot(liveNav);
  const { seoMetaTags, canonical } = cmsContent?.sportInfoPage ?? {};

  return (
    <Layout
      header={header}
      footer={footer}
      needHelp={needHelp}
      mightLikes={mightLikes}
      sidebarInstantGame={sidebarInstantGame}
    >
      <Seo siteSeo={site} metaTags={seoMetaTags} canonicalUrl={canonical} />
      <AmbienteContext.Provider value={{ pathScommessa: ['sport', 'scommessa'], ambiente: 'sport' }}>
        <PageContent {...cmsContent} seoData={seoData} staticMenu={staticMenu} />
      </AmbienteContext.Provider>
    </Layout>
  );
};

export default SportInfo;
